import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  serverTimestamp,
  doc,
  getDoc
} from 'firebase/firestore';

// Style guide colors
const PRIMARY_BROWN = '#C97B53';
const NEUTRAL_BG = '#F9F9F9';
const NEUTRAL_WHITE = '#FFF';
const NEUTRAL_TEXT = '#222';
const ACCENT_GRAY = '#757575';
const SUCCESS_GREEN = '#4CAF50';
const WARNING_ORANGE = '#FF9800';
const ERROR_RED = '#F44336';

const headingFont = { fontFamily: 'Sora, sans-serif', fontWeight: 700 };
const bodyFont = { fontFamily: 'Montserrat, sans-serif', fontWeight: 500 };

// Helper functions to handle trailing spaces and tab characters in field names
const getUserId = (orderData) => {
  if (orderData?.userId) return orderData.userId;
  if (orderData?.['userId ']) return orderData['userId ']; // Handle trailing space
  if (orderData?.['userId\t']) return orderData['userId\t']; // Handle tab character
  return undefined;
};

const getSellerId = (orderData) => {
  if (orderData?.sellerId) return orderData.sellerId;
  if (orderData?.['sellerId ']) return orderData['sellerId ']; // Handle trailing space
  if (orderData?.['sellerId\t']) return orderData['sellerId\t']; // Handle tab character
  return undefined;
};

export function OrderChat({ orderId, isOpen, onClose, order }) {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [orderDetails, setOrderDetails] = useState(null);
  const [sending, setSending] = useState(false);
  const [reporting, setReporting] = useState(null);
  const messagesEndRef = useRef(null);
  const [isTyping, setIsTyping] = useState(false);

  // OrderChat component rendered
  
  // Handle case where userId might have trailing space or tab in key name
  const userId = getUserId(order) || order?.['userId '] || order?.['userId\t'] || order?.userId;
  const sellerId = getSellerId(order) || order?.['sellerId '] || order?.['sellerId\t'] || order?.sellerId;
  
  // Cleaned user and seller IDs

  // Scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch order details
  useEffect(() => {
    if (!orderId || !isOpen) return;

    // If we have order data as a prop, use it first
    if (order && Object.keys(order).length > 0) {
      // Using order prop data
      // Clean the order data to handle trailing spaces in field names
      const cleanedOrder = {
        ...order,
        userId: getUserId(order),
        sellerId: getSellerId(order)
      };
              // Cleaned order data
      setOrderDetails(cleanedOrder);
      return;
    }

    const fetchOrderDetails = async () => {
      try {
        const orderRef = doc(db, 'orders', orderId);
        const orderSnap = await getDoc(orderRef);
        if (orderSnap.exists()) {
          const orderData = orderSnap.data();
          // Order details loaded from Firestore
          setOrderDetails(orderData);
        } else {
          console.error('Order not found:', orderId);
          setError('Order not found');
        }
      } catch (err) {
        console.error('Error fetching order details:', err);
        setError('Failed to load order details');
      }
    };

    fetchOrderDetails();
  }, [orderId, isOpen, order]);

  // Subscribe to messages for this order
  useEffect(() => {
    if (!orderId || !isOpen || !orderDetails) return;

    setLoading(true);
    setError('');

    // Only subscribe to messages if we have order details and can verify user participation
    const isFoodie = currentUser?.uid === orderDetails?.userId;
    const isSeller = currentUser?.uid === orderDetails?.sellerId || currentUser?.uid === orderDetails?.shopId || currentUser?.uid === orderDetails?.creatorId;
    
    if (!isFoodie && !isSeller) {
      // User is not a participant in this order, skipping message subscription
      setError('You do not have permission to view messages for this order');
      setLoading(false);
      return;
    }

          // Subscribing to messages for order

    const messagesQuery = query(
      collection(db, 'orderMessages'),
      where('orderId', '==', orderId),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(messagesQuery, (snapshot) => {
      const messageList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter messages client-side to ensure user only sees messages they should have access to
      const filteredMessages = messageList.filter(message => 
        message.senderId === currentUser?.uid || 
        message.receiverId === currentUser?.uid
      );
      
              // Messages loaded and filtered
      setMessages(filteredMessages);
      setLoading(false);
    }, (err) => {
      console.error('Error listening to messages:', err);
      setError('Failed to load messages');
      setLoading(false);
    });

    return unsubscribe;
  }, [orderId, isOpen, orderDetails, currentUser?.uid]);

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser?.uid || !orderId) return;
    
    // Ensure we have order details
    if (!orderDetails) {
      setError('Order details not loaded. Please try again.');
      return;
    }

    // Check if user is a participant in this order
    const isFoodie = currentUser.uid === orderDetails?.userId;
    const isSeller = currentUser.uid === orderDetails?.sellerId || currentUser.uid === orderDetails?.shopId || currentUser.uid === orderDetails?.creatorId;
    
    if (!isFoodie && !isSeller) {
      setError('You do not have permission to send messages for this order');
      return;
    }

    const messageText = newMessage.trim();
    setNewMessage('');
    setIsTyping(true);

    try {
      // Role detection already done above, reuse the variables
      const senderId = currentUser.uid;
      
      // For foodies, send to seller; for sellers, send to customer
      let receiverId;
      if (isFoodie) {
        // If user is foodie, message goes to seller
        receiverId = orderDetails?.sellerId || orderDetails?.shopId || orderDetails?.creatorId;
        // User identified as foodie, sending to seller
      } else if (isSeller) {
        // If user is seller/creator, message goes to customer
        receiverId = orderDetails?.userId || orderDetails?.customerId;
        // User identified as seller, sending to customer
        
        // Handle case where seller is messaging about their own order (testing scenario)
        if (orderDetails?.userId === currentUser.uid) {
          // Seller messaging about their own order - this is a testing scenario
          // For testing, allow seller to send messages to themselves
          receiverId = currentUser.uid;
                      // Updated receiverId to current user for testing
        }
      } else {
        // Fallback: if we can't determine role, assume current user is seller
                  // Role unclear, assuming current user is seller
        receiverId = orderDetails?.userId || orderDetails?.customerId;
      }
      
      // Final receiverId determination completed

      // Handle edge case: if sender and receiver are the same (seller testing their own order)
      if (receiverId === currentUser.uid) {
        // Sender and receiver are the same (testing scenario). Creating self-message for record keeping
        // In testing scenarios, we'll still create the message but mark it as a self-message
        // This allows sellers to test the chat functionality
      }
      
      // Handle case where seller is messaging about an order they created with a test customer ID
      if (isSeller && orderDetails?.userId !== currentUser.uid) {
        // Seller messaging about order with test customer ID. This is a testing scenario
      }

      // Validate receiverId exists
      if (!receiverId) {
        console.error('Cannot determine receiver ID:', { orderDetails, currentUser: currentUser.uid });
        throw new Error('Cannot determine message recipient. Please contact support.');
      }
      
      // Message configuration completed

      const messageData = {
        orderId,
        senderId,
        receiverId,
        message: messageText,
        timestamp: serverTimestamp(),
        senderType: isFoodie ? 'foodie' : 'creator',
        senderName: currentUser.displayName || currentUser.email || 'User',
        // Add flag for testing scenarios where sender = receiver
        isSelfMessage: receiverId === currentUser.uid
      };

      // Sending message
      await addDoc(collection(db, 'orderMessages'), messageData);
      
      // Reset typing indicator
      setIsTyping(false);
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.message || 'Failed to send message');
      setIsTyping(false);
    }
  };

  // Filter out inappropriate content (basic implementation)
  const filterMessage = (message) => {
    const phoneRegex = /\b\d{3}[-.]?\d{3}[-.]?\d{4}\b/;
    const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;
    const paymentRegex = /(venmo|paypal|cash ?app|zelle|bitcoin|eth|wallet)/i;
    
    if (phoneRegex.test(message) || emailRegex.test(message) || paymentRegex.test(message)) {
      return message.replace(phoneRegex, '[PHONE]')
                   .replace(emailRegex, '[EMAIL]')
                   .replace(paymentRegex, '[PAYMENT]');
    }
    return message;
  };

  // Report message
  const handleReportMessage = async (messageId) => {
    try {
      await addDoc(collection(db, 'reportedMessages'), {
        messageId,
        orderId,
        reportedBy: currentUser.uid,
        reportedAt: serverTimestamp(),
        reason: 'Inappropriate content'
      });
      alert('Message reported. Thank you for helping keep our community safe.');
    } catch (err) {
      console.error('Error reporting message:', err);
      alert('Failed to report message');
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: NEUTRAL_WHITE,
        borderRadius: '16px',
        width: '100%',
        maxWidth: '500px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: `1px solid ${NEUTRAL_BG}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h2 style={{ ...headingFont, fontSize: '18px', color: NEUTRAL_TEXT, margin: 0 }}>
              Order Chat
            </h2>
            {orderDetails && (
              <div style={{ fontSize: '14px', color: ACCENT_GRAY, marginTop: '4px' }}>
                Order #{orderId} • {orderDetails.items?.length || 0} items
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: ACCENT_GRAY,
              padding: '4px'
            }}
            aria-label="Close chat"
          >
            ×
          </button>
        </div>

        {/* Messages */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '20px',
          background: NEUTRAL_BG
        }}>
          {!orderDetails ? (
            <div style={{ textAlign: 'center', color: ACCENT_GRAY }}>
              Loading order details...
            </div>
          ) : loading ? (
            <div style={{ textAlign: 'center', color: ACCENT_GRAY }}>
              Loading messages...
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', color: ERROR_RED, padding: '20px' }}>
              <div style={{ fontSize: '16px', marginBottom: '8px' }}>⚠️ {error}</div>
              {error.includes('recipient') && (
                <div style={{ fontSize: '14px', marginTop: '8px' }}>
                  This might be due to missing order information. Please contact support.
                </div>
              )}
              {error.includes('permission') && (
                <div style={{ fontSize: '14px', marginTop: '8px' }}>
                  You may not have access to this order's messages.
                </div>
              )}
            </div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: ACCENT_GRAY, marginTop: '40px' }}>
              <div style={{ fontSize: '16px', marginBottom: '8px' }}>No messages yet</div>
                          <div style={{ fontSize: '14px' }}>
              {orderDetails?.sellerId === currentUser?.uid && orderDetails?.userId === currentUser?.uid 
                ? 'Testing mode: You can send messages to yourself to test the chat functionality'
                : 'Start the conversation about your order!'
              }
            </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {messages.map((message) => {
                const isOwnMessage = message.senderId === currentUser?.uid;
                const isFoodie = message.senderType === 'foodie';
                const isSelfMessage = message.isSelfMessage;
                
                return (
                  <div
                    key={message.id}
                    style={{
                      display: 'flex',
                      justifyContent: isOwnMessage ? 'flex-end' : 'flex-start'
                    }}
                  >
                    <div style={{
                      maxWidth: '70%',
                      background: isOwnMessage 
                        ? (isSelfMessage ? '#F0F0F0' : PRIMARY_BROWN)
                        : isFoodie 
                          ? '#E3F2FD' 
                          : '#F3E5F5',
                      color: isOwnMessage ? (isSelfMessage ? NEUTRAL_TEXT : NEUTRAL_WHITE) : NEUTRAL_TEXT,
                      padding: '12px 16px',
                      borderRadius: '18px',
                      fontSize: '14px',
                      position: 'relative',
                      border: isSelfMessage ? '1px dashed #CCC' : 'none'
                    }}>
                      {isSelfMessage && (
                        <div style={{
                          fontSize: '10px',
                          color: ACCENT_GRAY,
                          marginBottom: '4px',
                          fontStyle: 'italic'
                        }}>
                          📝 Test message
                        </div>
                      )}
                      <div style={{ marginBottom: '4px' }}>
                        {filterMessage(message.message)}
                      </div>
                      <div style={{
                        fontSize: '11px',
                        opacity: 0.7,
                        textAlign: 'right'
                      }}>
                        {message.timestamp?.toDate ? 
                          message.timestamp.toDate().toLocaleTimeString([], { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          }) :
                          new Date(message.timestamp).toLocaleTimeString([], { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })
                        }
                      </div>
                      
                      {/* Report button for non-own messages */}
                      {!isOwnMessage && (
                        <button
                          onClick={() => handleReportMessage(message.id)}
                          style={{
                            position: 'absolute',
                            top: '-8px',
                            right: '-8px',
                            background: ERROR_RED,
                            color: NEUTRAL_WHITE,
                            border: 'none',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          aria-label="Report message"
                          title="Report inappropriate message"
                        >
                          ⚠️
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {/* Typing indicator */}
              {isTyping && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-start'
                }}>
                  <div style={{
                    background: '#E3F2FD',
                    padding: '12px 16px',
                    borderRadius: '18px',
                    fontSize: '14px',
                    color: ACCENT_GRAY
                  }}>
                    Typing...
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Message Input */}
        <form onSubmit={handleSendMessage} style={{
          padding: '20px',
          borderTop: `1px solid ${NEUTRAL_BG}`,
          background: NEUTRAL_WHITE
        }}>
          <div style={{
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-end'
          }}>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              style={{
                flex: 1,
                padding: '12px 16px',
                border: `1px solid ${NEUTRAL_BG}`,
                borderRadius: '24px',
                fontSize: '14px',
                outline: 'none',
                fontFamily: 'inherit'
              }}
              maxLength={500}
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || !orderDetails || !!error}
              style={{
                background: (newMessage.trim() && orderDetails && !error) ? PRIMARY_BROWN : ACCENT_GRAY,
                color: NEUTRAL_WHITE,
                border: 'none',
                borderRadius: '24px',
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: (newMessage.trim() && orderDetails && !error) ? 'pointer' : 'not-allowed',
                fontFamily: 'inherit'
              }}
            >
              {!orderDetails ? 'Loading...' : error ? 'Error' : 'Send'}
            </button>
          </div>
          <div style={{
            fontSize: '11px',
            color: ACCENT_GRAY,
            textAlign: 'center',
            marginTop: '8px'
          }}>
            Messages are tied to this order and stored securely
          </div>
        </form>
      </div>
    </div>
  );
}
